import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { resolveImageUrl } from '../utils/shopHelpers';
import './TrustedClientsSection.css';

function ClientLogoCard({ logo, delay, fadeRef }) {
  const imageUrl = resolveImageUrl(logo.logo_url, logo.name);
  const card = (
    <div className="trusted-clients-card">
      <img className="trusted-clients-card__img" src={imageUrl} alt={logo.name} loading="lazy" decoding="async" />
    </div>
  );

  return (
    <div
      className="trusted-clients-item fade-in"
      style={{ transitionDelay: `${delay}s` }}
      ref={fadeRef}
    >
      {logo.website_url ? (
        <a
          href={logo.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className="trusted-clients-link"
          aria-label={logo.name}
        >
          {card}
        </a>
      ) : (
        card
      )}
    </div>
  );
}

export default function TrustedClientsSection({ fadeElementsRef }) {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicClientLogos();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setLogos(list);
      } catch {
        if (!cancelled) setLogos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const useMarquee = logos.length > 6;
  const marqueeTrack = useMarquee ? [...logos, ...logos] : logos;

  return (
    <section className="trusted-clients-section py-12 bg-black bg-opacity-50" aria-label="Trusted clients">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p
          className="text-center text-sm font-semibold uppercase text-gray-400 tracking-wider mb-8 fade-in"
          ref={(el) => fadeElementsRef.current.push(el)}
        >
          Trusted by industry leaders across Africa
        </p>

        {loading && (
          <div className="trusted-clients-grid" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`client-skeleton-${index}`} className="trusted-clients-item">
                <div className="trusted-clients-card trusted-clients-card--skeleton animate-pulse" />
              </div>
            ))}
          </div>
        )}

        {!loading && logos.length === 0 && null}

        {!loading && logos.length > 0 && !useMarquee && (
          <div className="trusted-clients-grid">
            {logos.map((logo, index) => (
              <ClientLogoCard
                key={logo.id}
                logo={logo}
                delay={0.1 + index * 0.1}
                fadeRef={(el) => fadeElementsRef.current.push(el)}
              />
            ))}
          </div>
        )}

        {!loading && logos.length > 0 && useMarquee && (
          <div className="trusted-clients-marquee" aria-label="Client logos carousel">
            <div className="trusted-clients-marquee__track">
              {marqueeTrack.map((logo, index) => (
                <ClientLogoCard
                  key={`${logo.id}-${index}`}
                  logo={logo}
                  delay={0}
                  fadeRef={() => {}}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
